// import React, { useEffect, useState } from "react";
import React, { useEffect, useMemo, useState } from "react";
import Table from "../../components/table/Table";
import moment from "moment";
import MySpinner from "../../components/Spinner/spinner";
import { salesReport, expensesReport, disposingReport, stockingReport, expiringReport, profitReport, itemsStockingReport } from "../../app/reportsSlice";
import { useDispatch, useSelector } from "react-redux";
import AppTabs from "../../components/appTabs/appTabs";
import Sections from "../../components/dashbord/sections";
import { instCat } from "../../common/validators";
import "./reports.css";
import Inputs from "../../components/inputs/Inputs";
import SalesReport from "./SalesReport";
import { getBankTransactionsReport, getDailySalesReport } from "../../api/reportsAPI";
import DailyInventory from "./DailyInventory";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { toast } from "react-toastify";
import { postBankTransaction } from "../../api/expensesAPI";

const toNumber = (value) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const formatNumber = (value) => {
  return toNumber(value).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

const sumReportAmounts = (list = []) => {
  if (!Array.isArray(list)) {
    return 0;
  }

  return list.reduce(
    (total, item) => total + toNumber(item?.amount),
    0
  );
};

const getPeriodLabel = (list = []) => {
  if (!Array.isArray(list) || list.length === 0) {
    return "Current reporting period";
  }

  const firstPeriod = list[0]?.time_bound;
  const lastPeriod = list[list.length - 1]?.time_bound;

  if (!firstPeriod) {
    return "Current reporting period";
  }

  if (!lastPeriod || firstPeriod === lastPeriod) {
    return firstPeriod;
  }

  return `${firstPeriod} – ${lastPeriod}`;
};

const RevenueKpiCard = ({
  label,
  value,
  description,
  tone = "blue",
  isNegative = false,
}) => {
  return (
    <article className={`revenue-kpi-card revenue-kpi-card--${tone}`}>
      <div className="revenue-kpi-card__top">
        <span className="revenue-kpi-card__label">{label}</span>
        <span className="revenue-kpi-card__dot" />
      </div>

      <strong
        className={
          isNegative
            ? "revenue-kpi-card__value revenue-kpi-card__value--negative"
            : "revenue-kpi-card__value"
        }
      >
        {formatNumber(value)}
      </strong>

      <span className="revenue-kpi-card__description">
        {description}
      </span>
    </article>
  );
};

const RevenueBreakdownCard = ({
  title,
  subtitle,
  data,
  isLoading,
  tone = "blue",
  emptyMessage = "No report data available",
}) => {
  const safeData = Array.isArray(data) ? data : [];

  return (
    <section className="revenue-breakdown-card">
      <header className="revenue-breakdown-card__header">
        <div>
          <h4>{title}</h4>
          <p>{subtitle}</p>
        </div>

        <span
          className={`revenue-breakdown-card__badge revenue-breakdown-card__badge--${tone}`}
        >
          {safeData.length} records
        </span>
      </header>

      {isLoading ? (
        <div className="revenue-breakdown-card__loading">
          <MySpinner size={30} color="#367588" />
        </div>
      ) : safeData.length > 0 ? (
        <div className="revenue-breakdown-card__table">
          <Sections
            columns={[
              { label: "Period", name: "time_bound" },
              { label: "Amount", name: "amount" },
            ]}
            data={safeData}
          />
        </div>
      ) : (
        <div className="revenue-breakdown-card__empty">
          {emptyMessage}
        </div>
      )}
    </section>
  );
};


const Reports = () => {


  const institutionCategory = useMemo(() => instCat(), []);

  const isFloatBusiness = institutionCategory === 4;
  const isServiceBusiness = institutionCategory === 5;

  const tabs = institutionCategory === 4 ?
    [
      {
        title: "Revenue",
      },
      {
        title: "Others",
      },
    ]
    :
    [
      {
        title: "Revenue",
      },
      {
        title: "Items Expiring",
      },
      {
        title: "Items Stocking",
      },
      {
        title: "Sales Report",
      },      
      {
        title: "Inventory Report",
      },          
      {
        title: "Bank Balance",
      },
    ];

  const itemsTableHead = [
    { label: "Period", name: "time_bound" },
    { label: "Amount", name: "amount" }
  ];

  const expiringTableHead = [
    { label: "Item Name", name: "item_description" },
    { label: "Item Code", name: "item_code" },
    { label: "Quantity", name: "qty" },
    { label: "Amount", name: "amount" },
    { label: "Purchase Date", name: "buy_date" },
    { label: "Expiry Date", name: "expiry_date" },
  ];

  const stockedTableHead = [
    { label: "Entry Date", name: "entry_date" },
    { label: "Item Name", name: "item_description" },
    { label: "Item Code", name: "item_code" },
    { label: "Quantity", name: "qty" },
    { label: "Buy Price", name: "buying_price" },
    { label: "Amount", name: "amount" },
    { label: "Purchase Date", name: "buy_date" },
  ];

  const bankTableHead = [
    { label: "Date", name: "txn_date" },
    { label: "Open Balance", name: "open_balance" },
    { label: "Deposits", name: "deposits" },
    { label: "Withdraws", name: "withdraws" },
    { label: "Charges", name: "charges" },
    { label: "Closing Balance", name: "closing_balance" },
  ];


  const [activeTab, setActiveTab] = useState(0);
  let [showRevenue, setShowRevenue] = useState(false);
  let [showExpenses, setShowExpenses] = useState(false);
  let [showBank, setShowBank] = useState(false);
  let [showDisposed, setShowDisposed] = useState(false);
  let [showSales, setShowSales] = useState(false);
  let [showOther, setShowOther] = useState(false);
  let [showInventory, setShowInventory] = useState(false);
  let [hideSaveButton, setHideSaveButton] = useState(false);
  let [bankModalForm, setBankModalForm] = useState(false);
  let [posting, setPosting] = useState(false);
  
  // const [formValues, setFormValues] = useState({});
  const [expiringDataList, setExpiringDataList] = useState([]);
  const [stockDataList, setStockDataList] = useState([]);
  const [bankTransactions, setBankTransactions] = useState([]);  
  const [salesReportByDate, setSalesReportByDate] = useState([]);
  const [errors, setErrors] = useState([]);
  const [formValues, setFormValues] = useState({ txnDate: moment().format("YYYY-MM-DD"), fromDate: moment().subtract(3, 'days').format("YYYY-MM-DD"), toDate: moment().format("YYYY-MM-DD") });




  const dispatch = useDispatch();

  const { error: salesError, status: salesStatus, list: salesList } = useSelector(
    ({ reports }) => reports.sales.get
  );

  const { error: expensesError, status: expensesStatus, list: expensesList } = useSelector(
    ({ reports }) => reports.expenses.get
  );

  const { error: disposingError, status: disposingStatus, list: disposingList } = useSelector(
    ({ reports }) => reports.disposing.get
  );

  const { error: stockingError, status: stockingStatus, list: stockingList } = useSelector(
    ({ reports }) => reports.stocking.get
  );

  const { error: itemsStockingError, status: itemsStockingStatus, list: itemsStockingList } = useSelector(
    ({ reports }) => reports.itemsStocking.get
  );

  const { error: expiringError, status: expiringStatus, list: expiringList } = useSelector(
    ({ reports }) => reports.expiring.get
  );

  const { error: profitError, status: profitStatus, list: profitList } = useSelector(
    ({ reports }) => reports.profit.get
  );

  const revenueSummary = useMemo(() => {
    const totalSales = sumReportAmounts(salesList);
    const totalProfit = sumReportAmounts(profitList);
    const totalExpenses = sumReportAmounts(expensesList);
    const totalDisposed = sumReportAmounts(disposingList);
    const totalStocking = sumReportAmounts(stockingList);

    // For normal businesses:
    // net result = profit - expenses - disposal value.
    //
    // For float institutions:
    // "profit" is commissions, so net result = commissions - expenses.
    const netResult = isFloatBusiness
      ? totalProfit - totalExpenses
      : totalProfit - totalExpenses - totalDisposed;

    return {
      totalSales,
      totalProfit,
      totalExpenses,
      totalDisposed,
      totalStocking,
      netResult,
      period: getPeriodLabel(salesList),
    };
  }, [
    salesList,
    profitList,
    expensesList,
    disposingList,
    stockingList,
    isFloatBusiness,
  ]);

  useEffect(() => {
    if (activeTab === 0) {
      setShowRevenue(true);
      setShowDisposed(false);
      setShowOther(false);
      setShowSales(false)
      setShowInventory(false)
      setShowBank(false)
      dispatch(salesReport())
      dispatch(expensesReport())
      dispatch(disposingReport())
      dispatch(profitReport())
      dispatch(stockingReport())
    } else if (institutionCategory != 4 && activeTab === 1) {
      setShowRevenue(false);
      setShowDisposed(true);
      setShowSales(false)
      setShowOther(false);
      setShowInventory(false)
      setShowBank(false)
      dispatch(expiringReport())
    } else if (institutionCategory != 4 && activeTab === 3) {
      setShowRevenue(false);
      setShowDisposed(false);
      setShowOther(false);
      setShowSales(true)
      setShowInventory(false)
      setShowBank(false)
      // dispatch(expiringReport())
    }else if (institutionCategory != 4 && activeTab === 4) {
      setShowRevenue(false);
      setShowDisposed(false);
      setShowOther(false);
      setShowSales(false)
      setShowInventory(true)
      setShowBank(false)
      // dispatch(expiringReport())
    }else if (institutionCategory != 4 && activeTab === 5) {
      setShowRevenue(false);
      setShowDisposed(false);
      setShowOther(false);
      setShowSales(false)
      setShowInventory(false)
      setShowBank(true)
      // dispatch(expiringReport())
    } else {
      setShowRevenue(false);
      setShowDisposed(false);
      setShowSales(false)
      setShowOther(true);
      setShowInventory(false)
      setShowBank(false)
      dispatch(itemsStockingReport({ startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'), endDate: moment().format('YYYY-MM-DD') }))
    }

  }, [dispatch, activeTab]);

  useEffect(() => {
    if(showBank === true){
      loadBankTransactions()
    }
  },[showBank])

  useEffect(() => {
    if (expiringStatus === 'idle' && !expiringError && expiringList) {
      let expiringArray = [];
      for (let index = 0; index < expiringList.length; index++) {
        expiringArray.push(
          {
            item_code: expiringList[index]?.item_code,
            item_description: expiringList[index]?.item_description,
            qty: Number(expiringList[index]?.qty).toLocaleString(),
            amount: Number(expiringList[index]?.qty * expiringList[index]?.buying_price).toLocaleString(),
            expiry_date: moment(expiringList[index]?.expiry_date).format("YYYY-MM-DD"),
            buy_date: moment(expiringList[index]?.buy_date).format("YYYY-MM-DD"),
          })

      }
      setExpiringDataList(expiringArray);

    }
  }, [expiringList, expiringStatus, expiringError])

  useEffect(() => {
    if (itemsStockingStatus === 'idle' && !itemsStockingError && itemsStockingList) {
      let stockArray = [];
      for (let index = 0; index < itemsStockingList.length; index++) {
        stockArray.push(
          {
            item_code: itemsStockingList[index]?.item_code,
            item_description: itemsStockingList[index]?.item_description,
            qty: Number(itemsStockingList[index]?.qty).toLocaleString(),
            buying_price: Number(itemsStockingList[index]?.buying_price).toLocaleString(),
            amount: Number(itemsStockingList[index]?.qty * itemsStockingList[index]?.buying_price).toLocaleString(),
            entry_date: moment(itemsStockingList[index]?.entry_date).format("YYYY-MM-DD"),
            buy_date: moment(itemsStockingList[index]?.buy_date).format("YYYY-MM-DD"),
          })

      }
      setStockDataList(stockArray);

    }
  }, [itemsStockingList, itemsStockingStatus, itemsStockingError])




  const loadBankTransactions = async () => {
    let result = await getBankTransactionsReport({ startDate: formValues.fromDate, endDate: formValues.toDate })
    if(result && result.data?.data?.length){
      let arrData = []
      for (let index = 0; index < result.data?.data.length; index++) {
        let element = result.data?.data[index];
        element.txn_date = moment(element?.txn_date).format('YYYY-MM-DD')
        element.deposits = Number(element?.deposits).toLocaleString()
        element.withdraws = Number(element?.withdraws).toLocaleString()
        element.charges = Number(element?.charges).toLocaleString()
        element.open_balance = Number(element?.open_balance).toLocaleString()
        element.closing_balance = Number(element?.closing_balance).toLocaleString()
        arrData.push(element)
      }
      setBankTransactions(arrData)
    }
  };

  const handleCloseModal = () => {
    setBankModalForm(false)
  }

  const handleSaveTransaction = async () => {
    setPosting(true)
    if(!formValues?.txnType){
      toast.error('Transaction Type is required');
    }else if(!formValues?.amount){
      toast.error('Amount is required');
    }else if(!formValues?.txnDate){
      toast.error('Transaction Date is required');
    }else if(1===2){

    }else{
      let newPayload = {
        ...formValues,
        txnDate: moment(formValues.txnDate).format('YYYY-MM-DD')
      }
      let result = await postBankTransaction(newPayload)
      setPosting(false)
      if(result.status === 201 && result.data?.mode === 'success'){
        toast.success('Successful')
        loadBankTransactions()
        setBankModalForm(false)
      }else{
        toast.error(result.data?.message ?? 'Failed to save, something went wrong');
      }      
    }

    
  }

  


  const formOpenSalesSearch = () => {
    return (
      <>
        <div className="row" style={{width:'100%'}}>
          <div className="col-sm-12 col-md-6 col-4">
            <Inputs
              name="fromDate"
              size="small"
              label="Start Date"
              value={formValues.fromDate}
              inputType="date"
              onChange={(e) => { handleChange({ target: { name: "fromDate", value: e } }) }}
              error={errors["fromDate"] ? true : false}
              helperText={errors["fromDate"] ? errors.fromDate[0] : null}
            />
          </div>
          <div className="col-sm-12 col-md-6 col-4">
            <Inputs
              name="toDate"
              size="small"
              label="End Date"
              value={formValues.toDate}
              inputType="date"
              onChange={(e) => { handleChange({ target: { name: "toDate", value: e } }) }}
              error={errors["toDate"] ? true : false}
              helperText={errors["toDate"] ? errors.toDate[0] : null}
            />
          </div>
          <div className="col-sm-12 col-md-6 col-4">
            <button style={{ padding: "15px", borderRadius: "5px" }} onClick={(e) => { e.preventDefault(); handleSearch() }}>
              Fetch Data
            </button>
            &nbsp;
            {
            showBank && 
            <button style={{ padding: "15px", borderRadius: "5px", backgroundColor: '#16a34a' }} onClick={(e) => { e.preventDefault(); setBankModalForm(true) }}>
              New Bank Record
            </button>
          }
          </div>
          
        </div>
      </>
    )
  }

  const formBankTransaction = () => {
    return (
      <>
        <div className="row" style={{width:'100%'}}>
          <div className="col-sm-12 col-md-6 col-6">
            <Inputs
              name="txnDate"
              size="small"
              label="Transaction Date"
              value={formValues.txnDate}
              inputType="date"
              maxDate={new Date()}
              onChange={(e) => { handleChange({ target: { name: "txnDate", value: e } }) }}
              error={errors["txnDate"] ? true : false}
              helperText={errors["txnDate"] ? errors.txnDate[0] : null}
            />
          </div>
          <div className="col-sm-12 col-md-6 col-6">
            <Inputs
              name="amount"
              size="small"
              label="Amount"
              value={formValues.amount}
              inputType="number"
              onChange={handleChange}    
              error={errors["amount"] ? true : false}
              helperText={errors["amount"] ? errors.amount[0] : null}
            />
          </div>

          <div className="col-sm-12 col-md-6 col-6">
            <Inputs
              name="txnType"
              size="small"
              label="Transaction Type"
              inputType="select"
              defaultValue={'deposit'}
              listLabel="Transaction Type"
              listValue="txnType"
              list={[{label: 'Deposit', value: 'deposit'}, {label: 'Withdraw', value: 'withdraw'}, {label: 'Charges', value: 'charges'}]}
              onChange={handleChange}          
            />
          </div>

          <div className="col-sm-12 col-md-6 col-6">
            <Inputs
              name="sellRef"
              size="small"
              label="Sale Reference(Optional)"
              value={formValues.sellRef}
              inputType="text"
              onChange={handleChange}                 
              error={errors["sellRef"] ? true : false}
              helperText={errors["sellRef"] ? errors.sellRef[0] : null}
            />
          </div>
          
        </div>
      </>
    )
  }

  const handleSearch = async () => {
    if(showBank){
      loadBankTransactions()
    }else{
      dispatch(itemsStockingReport({ startDate: formValues.fromDate, endDate: formValues.toDate }))
    }   

  }





  const handleChange = (event) => {
    setFormValues({
      ...formValues,
      [event.target.name]:
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    //toast.success(JSON.stringify(formValues));


  };


  const handleChangeTabs = (index) => {
    setActiveTab(index);
  };


  return (
    <div>
      <div className="ez-page-action">
        <h2 className="page-header">Reports</h2>

      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <AppTabs
                tabs={tabs}
                activeTab={activeTab}
                handleTabChange={handleChangeTabs}
              />

              {activeTab === 0 && (
                <div className="revenue-report">
                  <section className="revenue-report__hero">
                    <div>
                      <p className="revenue-report__eyebrow">
                        {isFloatBusiness ? "Transaction overview" : "Revenue overview"}
                      </p>

                      <h3 className="revenue-report__title">
                        {isFloatBusiness
                          ? "Transaction Performance"
                          : "Business Revenue Performance"}
                      </h3>

                      <p className="revenue-report__subtitle">
                        View sales, {isFloatBusiness ? "commissions" : "profit"}, expenses,
                        and inventory movement for the selected reporting period.
                      </p>
                    </div>

                    <div className="revenue-report__period">
                      <span>Reporting period</span>
                      <strong>{revenueSummary.period}</strong>
                    </div>
                  </section>

                  <section className="revenue-report__breakdown-grid">
                    <RevenueBreakdownCard
                      title={isFloatBusiness ? "Transactions" : "Sales"}
                      subtitle={
                        isFloatBusiness
                          ? "Transaction value by reporting period"
                          : "Sales value by reporting period"
                      }
                      data={salesList}
                      isLoading={salesStatus === "loading"}
                      tone="blue"
                      emptyMessage="No sales data found for this period."
                    />

                    <RevenueBreakdownCard
                      title={isFloatBusiness ? "Commissions" : "Gross Profit"}
                      subtitle={
                        isFloatBusiness
                          ? "Commissions earned by reporting period"
                          : "Profit before expenses by reporting period"
                      }
                      data={profitList}
                      isLoading={profitStatus === "loading"}
                      tone="green"
                      emptyMessage="No profit data found for this period."
                    />

                    <RevenueBreakdownCard
                      title="Expenses"
                      subtitle="Operating expenses by reporting period"
                      data={expensesList}
                      isLoading={expensesStatus === "loading"}
                      tone="orange"
                      emptyMessage="No expense data found for this period."
                    />

                    {!isServiceBusiness && !isFloatBusiness && (
                      <RevenueBreakdownCard
                        title="Disposing"
                        subtitle="Disposed inventory value by reporting period"
                        data={disposingList}
                        isLoading={disposingStatus === "loading"}
                        tone="red"
                        emptyMessage="No disposal data found for this period."
                      />
                    )}

                    <RevenueBreakdownCard
                      title={isFloatBusiness ? "Float Taking" : "Stocking"}
                      subtitle={
                        isFloatBusiness
                          ? "Float received by reporting period"
                          : "Stock received by reporting period"
                      }
                      data={stockingList}
                      isLoading={stockingStatus === "loading"}
                      tone="purple"
                      emptyMessage="No stocking data found for this period."
                    />
                  </section>
                </div>
              )}

              {institutionCategory != 5 && showDisposed && <div className="row">
                <div className="col-sm-12 col-md-12 col-12 report-sub-div">
                  <Table
                    columns={expiringTableHead}
                    data={expiringDataList}
                    downloadFileName={"Items Expiring Soon"}
                  />
                </div>
              </div>}

              {showOther && <div className="row">
                <div className="col-sm-12 col-md-12 col-12 report-sub-div">
                  {
                    formOpenSalesSearch()
                  }
                  <Table
                    columns={stockedTableHead}
                    data={stockDataList}
                    downloadFileName={"Items Expiring Soon"}
                  />
                </div>

              </div>}

              {showSales && <div className="row">
                <div className="col-sm-12 col-md-12 col-12 report-sub-div">
                  <SalesReport />
                </div>
              </div>}

              {showInventory && <div className="row">
                <div className="col-sm-12 col-md-12 col-12 report-sub-div">
                  <DailyInventory />
                </div>
              </div>}

               {showBank && <div className="row">
                <div className="col-sm-12 col-md-12 col-12 report-sub-div">
                  {
                    formOpenSalesSearch()
                  }
                  <Table
                    columns={bankTableHead}
                    data={bankTransactions}
                    downloadFileName={"Bank Report"}
                  />
                </div>

              </div>}

              <Dialog open={bankModalForm} fullWidth={true}>
                <DialogTitle>
                  Bank Transactions
                </DialogTitle>
                <DialogContent >
                  <DialogContentText>
                      {
                        formBankTransaction()
                      }
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Inputs
                      color="primary"
                      size="small"
                      inputType="button"
                      text="Close"
                      type="submit"
                      onClick={handleCloseModal}
                    />


                    <Inputs
                      color="primary"
                      size="small"
                      inputType="button"
                      disabled={posting}
                      text={posting ? 'Posting ...' : 'Save'}
                      type="submit"
                      propBackgroundColor={'green'}
                      onClick={posting === false ? handleSaveTransaction : null}
                    />
                </DialogActions>
              </Dialog>

            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Reports;
